import { FiltersChangedEvent } from '@theme/events';

class FilterComponent extends HTMLElement {
  constructor() {
    super();

    this.form = null;
  }

  connectedCallback() {
    this.form = this.querySelector('[data-ref="filters-form"]');

    this.addEventListener('change', this.#onFiltersChanged);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onFiltersChanged);
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    if (!event.target.closest('[data-ref="view-selection"]')) return;

    event.preventDefault();

    this.closest('side-menu-component')?.close();

    const section = this.closest('.shopify-section');
    const products = section?.querySelector('[data-ref="main-collection-products"], [data-ref="main-search-products"]');
    if (!products) return;

    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const targetY = products.getBoundingClientRect().top + window.scrollY - headerHeight;

    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  };

  #onFiltersChanged = (event) => {
    event.preventDefault();

    const filterParams = this.getFiltersParams();
    const sortingParams = this.closest('filter-and-sort-component').getCurrentSortingParams();

    document.dispatchEvent(new FiltersChangedEvent(filterParams, sortingParams));
  };

  getFiltersParams() {
    const formData = new FormData(this.form);
    const newParameters = new URLSearchParams(formData);

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    return newParameters.toString();
  }

  renderFilters(newFiltersHtml) {
    this.innerHTML = newFiltersHtml.innerHTML;
    this.form = this.querySelector('[data-ref="filters-form"]');
  }
}

class FilterListComponent extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-ref="show-more-toggle"]');
    if (!this.toggle) return;

    this.toggle.addEventListener('click', this.#onToggle);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.#onToggle);
  }

  #onToggle = () => {
    const isExpanded = this.classList.toggle('filter-list--expanded');
    this.toggle.textContent = isExpanded ? this.toggle.dataset.lessText : this.toggle.dataset.moreText;
  };
}

class FilterPriceRangeComponent extends HTMLElement {
  #rangeMax = 0;
  #currencySymbol = '€';

  connectedCallback() {
    this.minRange = this.querySelector('[data-ref="min-range"]');
    this.maxRange = this.querySelector('[data-ref="max-range"]');
    this.minInput = this.querySelector('[data-ref="min-input"]');
    this.maxInput = this.querySelector('[data-ref="max-input"]');
    this.trackFill = this.querySelector('[data-ref="track-fill"]');
    this.labelValueMin = this.querySelector('[data-ref="label-value-min"]');
    this.labelValueMax = this.querySelector('[data-ref="label-value-max"]');

    if (!this.minRange || !this.maxRange) return;

    this.#rangeMax = parseFloat(this.dataset.rangeMax) || 0;
    this.#currencySymbol = this.dataset.currencySymbol || '€';

    this.minRange.addEventListener('input', this.#onMinInput);
    this.maxRange.addEventListener('input', this.#onMaxInput);
    this.minRange.addEventListener('change', this.#onChange);
    this.maxRange.addEventListener('change', this.#onChange);

    this.#updateUI();
  }

  disconnectedCallback() {
    this.minRange?.removeEventListener('input', this.#onMinInput);
    this.maxRange?.removeEventListener('input', this.#onMaxInput);
    this.minRange?.removeEventListener('change', this.#onChange);
    this.maxRange?.removeEventListener('change', this.#onChange);
  }

  #onMinInput = () => {
    const minValue = parseFloat(this.minRange.value);
    const maxValue = parseFloat(this.maxRange.value);

    if (minValue > maxValue) {
      this.minRange.value = maxValue;
    }

    this.#updateUI(false);
  };

  #onMaxInput = () => {
    const minValue = parseFloat(this.minRange.value);
    const maxValue = parseFloat(this.maxRange.value);

    if (maxValue < minValue) {
      this.maxRange.value = minValue;
    }

    this.#updateUI(false);
  };

  #onChange = () => {
    this.#updateHiddenInputs();
    this.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #updateUI(updateHiddenInputs = true) {
    const minValue = parseFloat(this.minRange.value);
    const maxValue = parseFloat(this.maxRange.value);
    const range = this.#rangeMax || 1;
    const minPercent = (minValue / range) * 100;
    const maxPercent = (maxValue / range) * 100;

    this.trackFill.style.left = `${minPercent}%`;
    this.trackFill.style.width = `${maxPercent - minPercent}%`;

    this.#positionLabel(this.labelValueMin, minPercent);
    this.#positionLabel(this.labelValueMax, maxPercent);

    this.labelValueMin.textContent = this.#formatPrice(minValue);
    this.labelValueMax.textContent = this.#formatPrice(maxValue);

    if (updateHiddenInputs) {
      this.#updateHiddenInputs();
    }
  }

  #positionLabel(label, percent) {
    if (!label) return;
    label.style.left = `${percent}%`;
  }

  #updateHiddenInputs() {
    const minValue = parseFloat(this.minRange.value);
    const maxValue = parseFloat(this.maxRange.value);

    this.minInput.value = minValue > 0 ? this.#formatInputValue(minValue) : '';
    this.maxInput.value = maxValue < this.#rangeMax ? this.#formatInputValue(maxValue) : '';
  }

  #formatInputValue(value) {
    return String(Math.round(value));
  }

  #formatPrice(value) {
    return `${Math.round(value)}${this.#currencySymbol}`;
  }
}

if (!customElements.get('filter-component')) {
  customElements.define('filter-component', FilterComponent);
}

if (!customElements.get('filter-list-component')) {
  customElements.define('filter-list-component', FilterListComponent);
}

if (!customElements.get('filter-price-range-component')) {
  customElements.define('filter-price-range-component', FilterPriceRangeComponent);
}
